import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import adminApi, { saveAdminToken } from '@/lib/adminApi';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const AdminLogin = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [turnstileContainerId] = useState(() => `admin-turnstile-${Math.random().toString(36).slice(2)}`);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkExistingSession = async () => {
      try {
        await adminApi.get('/admin/me');
        if (mounted) {
          navigate('/admin', { replace: true });
        }
      } catch (requestError) {
        // No existing session is fine here.
      }
    };

    checkExistingSession();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (challengeId || !TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    let widgetId = null;

    const renderTurnstile = () => {
      const container = document.getElementById(turnstileContainerId);
      if (cancelled || !window.turnstile || !container || container.childNodes.length) return;
      widgetId = window.turnstile.render(`#${turnstileContainerId}`, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
    };

    if (!document.querySelector('script[data-turnstile-script]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.onload = renderTurnstile;
      document.head.appendChild(script);
    } else {
      renderTurnstile();
    }

    return () => {
      cancelled = true;
      if (window.turnstile && widgetId !== null) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [challengeId, turnstileContainerId]);

  const handleChange = (event) => {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!TURNSTILE_SITE_KEY) {
        throw new Error('Admin CAPTCHA is not configured.');
      }
      if (!captchaToken) {
        throw new Error('Please complete the security check.');
      }
      const response = await adminApi.post('/admin/login', { ...form, turnstile_token: captchaToken });
      if (response.data?.mfa_required) {
        setChallengeId(response.data.challenge_id);
        return;
      }
      saveAdminToken();
      const destination = location.state?.from?.pathname || '/admin';
      navigate(destination, { replace: true });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to sign in.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await adminApi.post('/admin/login/verify', { challenge_id: challengeId, code: mfaCode });
      saveAdminToken();
      const destination = location.state?.from?.pathname || '/admin';
      navigate(destination, { replace: true });
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((item) => item?.msg || item?.message || String(item)).join('; '));
      } else {
        setError(requestError.message || 'Unable to verify the code.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 bg-white rounded-[2rem] overflow-hidden shadow-2xl">
        <section className="hidden lg:flex flex-col justify-between p-10 text-white gradient-bg">
          <div>
            <p className="uppercase tracking-[0.35em] text-xs text-orange-100">Kala Manch</p>
            <h1 className="text-5xl font-bold mt-6 leading-tight">Admin access for the creative platform team.</h1>
            <p className="mt-6 text-lg text-orange-50">
              Review registrations, manage videos, and keep platform settings organized without touching the public experience.
            </p>
          </div>
          <div className="glass-effect rounded-3xl p-6">
            <p className="text-sm text-white/90">
              Configure `ADMIN_EMAIL`, `ADMIN_PASSWORD`, and `ADMIN_JWT_SECRET` in the backend environment to sign in safely.
            </p>
          </div>
        </section>

        <section className="p-6 sm:p-10 flex items-center">
          <div className="w-full">
            <div className="mb-8">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Admin Login</p>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">Welcome back</h2>
              <p className="text-gray-600 mt-3">
                Sign in to the Kala Manch admin panel. Public artist and visitor flows stay separate.
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {!challengeId ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">
                  Admin Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#FF6F00]"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#FF6F00]"
                  placeholder="Enter your password"
                />
              </div>

              <div>
                <div id={turnstileContainerId} />
                {!TURNSTILE_SITE_KEY && (
                  <p className="text-sm text-red-600">Admin CAPTCHA configuration is missing.</p>
                )}
              </div>

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            ) : (
              <form onSubmit={handleMfaSubmit} className="space-y-5">
                <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
                  Enter the newest six-digit code sent to the administrator email. Requesting another code makes earlier codes invalid.
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="mfaCode">
                    Verification code
                  </label>
                  <input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    minLength="6"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-lg tracking-[0.35em] outline-none focus:ring-2 focus:ring-[#FF6F00]"
                    placeholder="000000"
                  />
                </div>
                <button type="submit" className="btn-primary w-full" disabled={loading || mfaCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify and sign in'}
                </button>
                <button
                  type="button"
                  className="w-full text-sm font-semibold text-[#4A148C]"
                  onClick={() => {
                    setChallengeId('');
                    setMfaCode('');
                    setCaptchaToken('');
                  }}
                >
                  Back to password sign-in
                </button>
              </form>
            )}

            <div className="mt-8 text-sm text-gray-500">
              <Link to="/" className="text-[#4A148C] font-semibold hover:text-[#FF6F00] transition-colors">
                Return to public website
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminLogin;
